import { LinkedMixedOperation } from 'interfaces/linkage'
import { State } from 'interfaces/state'
import { anyMixedLinkageEntitySelector, mixedAccountConfirmationSelector } from 'selectors/linkageSelectors'
import { connectAdvanced } from 'react-redux'
import { ConfirmModalProps, default as ConfirmModal } from 'components/features/pfm/ConfirmModal/ConfirmModal'
import { StoreDispatch } from 'interfaces/component'
import { confirmMixedAccountOperationActionCreator, performMixedAccountOperationActionCreator } from 'actions/linkageActions'
import { renderOptionally, createPositiveRendererProps, NO_RENDER, OptionalRendererProps } from 'tangle-shared'
import { captureWarning } from 'helpers/errorHelpers'

const getTextProps = (operation: LinkedMixedOperation): Pick<ConfirmModalProps, 'children' | 'confirmButtonText'> | null => {
  switch (operation) {
    case 'unlink':
      return {
        confirmButtonText: 'Unlink',
        children: 'Unlinking this account preserves its historical data, but stops new data refreshes. Are you sure you want to unlink this account?',
      }

    case 'delete':
      return {
        confirmButtonText: 'Delete',
        children: 'Deleting this account will permanently remove all of its historical data as well. Are you sure you want to delete this account?',
      }

    default:
      return null
  }
}

const selectorFactory = (dispatch: StoreDispatch) => {
  return (nextState: State): OptionalRendererProps<ConfirmModalProps> => {
    const confirmation = mixedAccountConfirmationSelector(nextState)
    if (!confirmation) {
      return NO_RENDER()
    }

    const { cid, operation } = confirmation
    const account = anyMixedLinkageEntitySelector(nextState, { cid })
    if (!account) {
      captureWarning(`No mixed entity found`, {
        source: `MixedAccountConfirmModalContainer`,
        cid,
      })
      return NO_RENDER()
    }

    const textProps = getTextProps(operation)
    if (!textProps) {
      return NO_RENDER()
    }

    const onConfirm = () => dispatch(performMixedAccountOperationActionCreator.request({ account, operation }))
    const onCancel = () => dispatch(confirmMixedAccountOperationActionCreator(account, null))

    return createPositiveRendererProps<ConfirmModalProps>({
      ...textProps,
      onConfirm,
      onCancel,
    })
  }
}

const MixedAccountConfirmModalContainer = connectAdvanced<
  State, OptionalRendererProps<ConfirmModalProps>, {}
>(selectorFactory)(renderOptionally(ConfirmModal))

export default MixedAccountConfirmModalContainer
